import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { API, cardsApi } from "@/lib/api";
import { downloadVCard, normalizeWebUrl } from "@/lib/vcard";
import {
  Phone, Mail, Globe, MapPin, Linkedin, Facebook, Share2, MoreVertical,
  Wallet, ChevronRight, Loader2,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function PublicCard() {
  const { slug } = useParams();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    cardsApi.getBySlug(slug)
      .then((c) => { if (!cancelled) setCard(c); })
      .catch(() => { if (!cancelled) setCard(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const departmentCompany = useMemo(
    () => card ? [card.department, card.company].filter(Boolean).join(" · ") : "",
    [card],
  );
  const fullAddress = useMemo(
    () => card ? [card.address, card.city, card.zip, card.country].filter(Boolean).join(", ") : "",
    [card],
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#07111D] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#18BFFF] animate-spin" />
      </div>
    );
  }

  if (!card) {
    return (
      <div className="min-h-screen bg-[#07111D] flex items-center justify-center text-center px-6">
        <div data-testid="card-not-found">
          <h1 className="font-display text-3xl text-white">Card not found</h1>
          <p className="mt-3 text-[#94A3B8]">The card &ldquo;{slug}&rdquo; doesn&apos;t exist or is not available.</p>
        </div>
      </div>
    );
  }

  const displayName = card.full_name || `${card.first_name} ${card.last_name}`.trim();
  const initials = card.initials || (displayName.split(" ").map(s => s[0]).slice(0, 2).join("") || "?").toUpperCase();
  const mobile = card.cell_phone || card.phone;

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: displayName, url });
      } catch (err) {
        // User dismissed the share sheet or share failed; log for debugging.
        console.warn("Share cancelled:", err);
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    }
  };

  const primary = card.primary_color || "#18BFFF";
  const headerColor = card.header_color || "#0B1F38";

  return (
    <div className="min-h-screen bg-[#07111D] flex items-start justify-center md:py-8">
      <div className="w-full max-w-md bg-[#07111D] shadow-[0_0_50px_rgba(0,0,0,0.5)] md:rounded-[40px] md:border-[8px] md:border-[#1E2D40] overflow-hidden relative pb-8">
        {/* Top bar with share + settings */}
        <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-5 pt-5">
          <button
            onClick={share}
            className="w-10 h-10 rounded-full bg-black/25 backdrop-blur flex items-center justify-center text-white hover:bg-black/40 transition-colors"
            data-testid="public-share-btn"
            aria-label="Share"
          >
            <Share2 className="w-5 h-5" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-10 h-10 rounded-full bg-black/25 backdrop-blur flex items-center justify-center text-white hover:bg-black/40 transition-colors"
                data-testid="public-settings-btn"
                aria-label="Options"
              >
                <MoreVertical className="w-5 h-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#101B28] border border-[#1E2D40] text-white">
              <DropdownMenuItem data-testid="menu-details" onClick={() => setDetailsOpen(true)}>Details</DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-open-browser" onClick={() => window.open(window.location.href, "_blank")}>Open in browser</DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-open-app" onClick={() => toast.info("Native app coming soon")}>Open in app</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Gradient header */}
        <div
          className="w-full h-48 md:h-56 relative overflow-hidden"
          style={{ background: `linear-gradient(180deg, ${primary} 0%, ${headerColor} 100%)` }}
        >
          {card.header_image && (
            <img src={card.header_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
          )}
        </div>

        {/* Avatar */}
        <div className="relative">
          <div
            className="absolute left-1/2 -translate-x-1/2 -top-14 w-28 h-28 rounded-full border-4 border-[#101B28] bg-[#07111D] flex items-center justify-center overflow-hidden shadow-xl z-10"
            data-testid="avatar"
          >
            {card.profile_image ? (
              <img src={card.profile_image} alt={displayName} className="w-full h-full object-cover" />
            ) : (
              <span className="font-display text-3xl font-bold text-white">{initials}</span>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="bg-[#101B28] rounded-t-3xl -mt-6 relative z-0 px-6 pt-20 pb-8 min-h-[520px] animate-fade-up">
          <div className="text-center">
            <h1 className="font-display text-2xl font-semibold text-white" data-testid="card-name">{displayName || "Unnamed"}</h1>
            {card.job_title && (
              <p className="mt-1 text-sm text-[#94A3B8]" data-testid="card-job">{card.job_title}</p>
            )}
            {(card.department || card.company) && (
              <p className="mt-0.5 text-sm text-[#94A3B8]" data-testid="card-company">
                {departmentCompany}
              </p>
            )}
          </div>

          {/* Save Contact */}
          <button
            onClick={() => downloadVCard(card)}
            data-testid="save-contact-btn"
            className="mt-8 w-full rounded-full bg-[#18BFFF] text-[#07111D] font-bold text-base py-4 hover:bg-[#4DD0FF] active:scale-[0.98] transition-all shadow-[0_4px_20px_rgba(24,191,255,0.3)]"
          >
            Save Contact
          </button>

          {/* Contact list */}
          <div className="mt-8 space-y-3" data-testid="contact-list">
            {mobile && (
              <ContactRow icon={Phone} label="Mobile" value={mobile} />
            )}
            {card.email && (
              <ContactRow icon={Mail} label="Email" value={card.email} />
            )}
            {card.website && (
              <ContactRow icon={Globe} label="Website" value={card.website.replace(/^https?:\/\//, "")} />
            )}
            {(card.address || card.city) && (
              <ContactRow icon={MapPin} label="Address" value={fullAddress} />
            )}
          </div>

          {/* Actions grid */}
          <div className="grid grid-cols-4 gap-3 mt-8" data-testid="action-grid">
            <Action icon={Phone} label="Call" href={mobile ? `tel:${mobile}` : undefined} disabled={!mobile} testid="action-call" />
            <Action icon={Mail} label="Email" href={card.email ? `mailto:${card.email}` : undefined} disabled={!card.email} testid="action-email" />
            <Action icon={Globe} label="Website" href={normalizeWebUrl(card.website) || undefined} disabled={!card.website} testid="action-website" />
            <Action icon={MapPin} label="Maps"
              href={card.address ? `https://maps.google.com/?q=${encodeURIComponent([card.address, card.city, card.country].filter(Boolean).join(", "))}` : undefined}
              disabled={!card.address} testid="action-maps" />
            <Action icon={Linkedin} label="LinkedIn" href={card.linkedin || undefined} disabled={!card.linkedin} testid="action-linkedin" />
            <Action icon={Facebook} label="Facebook" href={card.facebook || undefined} disabled={!card.facebook} testid="action-facebook" />
          </div>

          {/* Wallet buttons */}
          <div className="mt-10 grid grid-cols-2 gap-3" data-testid="wallet-buttons">
            <a
              href={`${API}/cards/${card.id}/apple-wallet`}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-xl border border-[#1E2D40] bg-black px-4 py-3 flex items-center justify-center gap-2 text-white hover:bg-[#111] transition-colors"
              data-testid="apple-wallet-btn"
            >
              <Wallet className="w-4 h-4" />
              <div className="text-left leading-tight">
                <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Add to</div>
                <div className="text-xs font-semibold">Apple Wallet</div>
              </div>
            </a>
            <button
              onClick={async () => {
                try {
                  const res = await fetch(`${API}/cards/${card.id}/google-wallet`);
                  const { saveUrl, dev_mode } = await res.json();
                  if (dev_mode) {
                    toast.warning("Google Wallet is in dev mode — real Issuer ID not configured yet.");
                  }
                  window.open(saveUrl, "_blank", "noopener,noreferrer");
                } catch (err) {
                  console.error(err);
                  toast.error("Failed to build Google Wallet link");
                }
              }}
              className="rounded-xl border border-[#1E2D40] bg-white text-[#07111D] px-4 py-3 flex items-center justify-center gap-2 hover:bg-[#f3f4f6] transition-colors"
              data-testid="google-wallet-btn"
            >
              <Wallet className="w-4 h-4" />
              <div className="text-left leading-tight">
                <div className="text-[10px] uppercase tracking-wider text-[#4A5A73]">Save to</div>
                <div className="text-xs font-semibold">Google Wallet</div>
              </div>
            </button>
          </div>

          {card.public_description && (
            <p className="mt-8 text-sm text-[#94A3B8] text-center leading-relaxed" data-testid="public-description">
              {card.public_description}
            </p>
          )}
        </div>
      </div>

      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="bg-[#101B28] border-[#1E2D40] text-white">
          <DialogHeader>
            <DialogTitle className="font-display">Card Details</DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-2">
            <Detail k="Slug" v={card.slug} />
            <Detail k="Full name" v={card.full_name} />
            <Detail k="Company" v={card.company} />
            <Detail k="Department" v={card.department} />
            <Detail k="Job title" v={card.job_title} />
            <Detail k="Country" v={card.country} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Action({ icon: Icon, label, href, disabled, testid }) {
  const cls = "flex flex-col items-center gap-2 group";
  const iconCls = disabled
    ? "w-14 h-14 rounded-full bg-[#162436]/50 flex items-center justify-center text-[#4A5A73]"
    : "w-14 h-14 rounded-full bg-[#162436] flex items-center justify-center text-[#18BFFF] hover:bg-[#18BFFF] hover:text-[#07111D] transition-colors";
  const textCls = disabled ? "text-xs text-[#4A5A73]" : "text-xs text-[#94A3B8]";
  if (disabled) {
    return (
      <div className={cls} data-testid={testid} aria-disabled>
        <div className={iconCls}><Icon className="w-5 h-5" /></div>
        <span className={textCls}>{label}</span>
      </div>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={testid}>
      <div className={iconCls}><Icon className="w-5 h-5" /></div>
      <span className={textCls}>{label}</span>
    </a>
  );
}

function ContactRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-4 rounded-xl bg-[#0B131E] border border-[#1E2D40] px-4 py-3">
      <div className="w-10 h-10 rounded-full bg-[#162436] flex items-center justify-center text-[#18BFFF]">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A73]">{label}</div>
        <div className="text-sm text-white truncate">{value}</div>
      </div>
      <ChevronRight className="w-4 h-4 text-[#4A5A73]" />
    </div>
  );
}

function Detail({ k, v }) {
  return (
    <div className="flex justify-between gap-4 border-b border-[#1E2D40] py-1.5">
      <span className="text-[#94A3B8]">{k}</span>
      <span className="text-white text-right">{v || "—"}</span>
    </div>
  );
}
